import type { LucideIcon } from 'lucide-react';

/**
 * The one icon size scale (px). Every icon in the system uses one of these —
 * components never pass a literal `size={17}`. Tuned to the type scale:
 * xs pairs with caption, sm/md with body, lg with titles, xl for featured
 * icons in headers / empty states.
 */
export const iconSizes = { xs: 14, sm: 16, md: 18, lg: 20, xl: 24 } as const;
export type IconSize = keyof typeof iconSizes;

export interface IconProps {
  /** A lucide icon component, e.g. `Search`. */
  as: LucideIcon;
  size?: IconSize;
  strokeWidth?: number;
  className?: string;
  /** Provide when the icon is the only label; otherwise it's decorative. */
  'aria-label'?: string;
}

/**
 * Icon primitive — wraps a lucide icon at a standard `size` so the whole app
 * shares one icon scale. Decorative by default (aria-hidden); pass `aria-label`
 * when the icon stands alone.
 */
export const Icon = ({
  as: Cmp,
  size = 'sm',
  strokeWidth = 2,
  className,
  ...rest
}: IconProps) => (
  <Cmp
    size={iconSizes[size]}
    strokeWidth={strokeWidth}
    className={className}
    aria-hidden={rest['aria-label'] ? undefined : true}
    aria-label={rest['aria-label']}
  />
);
